import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ChevronLeft, 
  Users, 
  Anchor, 
  Ship, 
  CalendarClock, 
  UserPlus,
  RefreshCw,
  Trophy,
  Activity
} from 'lucide-react';
import { getSystemAnalytics, SystemAnalytics } from '../services/adminAnalyticsService';
import toast from 'react-hot-toast';

const AdminAnalytics = () => {
  const [data, setData] = useState<SystemAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [linesPage, setLinesPage] = useState(1);
  const [shipsPage, setShipsPage] = useState(1);
  const itemsPerPage = 10;

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const result = await getSystemAnalytics();
      setData(result);
      setCurrentPage(1);
      setLinesPage(1);
      setShipsPage(1);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load system analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const StatCard = ({ title, value, icon, accent }: { title: string, value: string | number, icon: React.ReactNode, accent: string }) => (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-lg border border-gray-200 bg-white p-5 shadow-sm"
    >
      <div className={`absolute inset-0 bg-gradient-to-br ${accent}`} aria-hidden />
      <div className="relative flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="mt-1 text-3xl font-bold text-gray-900">{value}</p>
        </div>
        <div className="rounded-full bg-white/80 p-3 shadow-sm border border-gray-100">
          {icon}
        </div>
      </div>
    </motion.div>
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="max-w-6xl mx-auto space-y-6 pb-20"
    >
      <div className="flex items-center gap-4">
        <Link
          to="/admin"
          className="mr-4 p-2 rounded-full hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-accent"
          aria-label="Back to Admin Dashboard"
        >
          <ChevronLeft className="h-5 w-5 text-gray-600" />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
            <Activity className="h-8 w-8 text-violet-600" />
            System Analytics
          </h1>
          <p className="text-gray-600 mt-1">Platform usage and user engagement metrics</p>
        </div>
        <div className="ml-auto flex items-center">
          <button
            onClick={fetchAnalytics}
            disabled={loading}
            className="flex items-center gap-2 rounded-lg bg-white border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh Data
          </button>
        </div>
      </div>

      {loading && !data ? (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
        </div>
      ) : data ? (
        <div className="space-y-6">
          {/* Top Level Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard 
              title="Total Users" 
              value={data.totalUsers.toLocaleString()} 
              icon={<Users className="h-6 w-6 text-blue-600" />} 
              accent="from-blue-500/10 to-blue-500/0"
            />
            <StatCard 
              title="Total Cruises" 
              value={data.totalCruises.toLocaleString()} 
              icon={<Anchor className="h-6 w-6 text-emerald-600" />} 
              accent="from-emerald-500/10 to-emerald-500/0"
            />
            <StatCard 
              title="Users w/ Cruises" 
              value={data.usersWithCruises.toLocaleString()} 
              icon={<Activity className="h-6 w-6 text-indigo-600" />} 
              accent="from-indigo-500/10 to-indigo-500/0"
            />
            <StatCard 
              title="Total Friendships" 
              value={data.totalFriendships.toLocaleString()} 
              icon={<UserPlus className="h-6 w-6 text-purple-600" />} 
              accent="from-purple-500/10 to-purple-500/0"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Left Column: Breakdowns */}
            <div className="lg:col-span-2 space-y-6">
              {/* Cruise Timeline Breakdown */}
              <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <CalendarClock className="h-5 w-5 text-gray-500" />
                  Cruise Timeline
                </h3>
                
                <div className="flex items-center gap-4 mb-2">
                  <div className="flex-1">
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">Past Cruises</span>
                      <span className="text-sm font-bold text-gray-900">{data.pastCruises.toLocaleString()}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2.5">
                      <div className="bg-amber-500 h-2.5 rounded-full" style={{ width: `${(data.pastCruises / Math.max(data.totalCruises, 1)) * 100}%` }}></div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 mt-4">
                  <div className="flex-1">
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">Future Cruises</span>
                      <span className="text-sm font-bold text-gray-900">{data.futureCruises.toLocaleString()}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2.5">
                      <div className="bg-emerald-500 h-2.5 rounded-full" style={{ width: `${(data.futureCruises / Math.max(data.totalCruises, 1)) * 100}%` }}></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Top Cruise Lines & Ships */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm flex flex-col">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2 shrink-0">
                    <Ship className="h-5 w-5 text-gray-500" />
                    Top Cruise Lines
                  </h3>
                  <div className="space-y-3 flex-1">
                    {data.topCruiseLines
                      .slice((linesPage - 1) * itemsPerPage, linesPage * itemsPerPage)
                      .map((line, idx) => (
                        <div key={idx} className="flex justify-between items-center border-b border-gray-50 pb-2 last:border-0 last:pb-0">
                          <span className="text-sm text-gray-700 truncate pr-4">{line.name}</span>
                          <span className="text-sm font-semibold bg-gray-100 px-2 py-0.5 rounded text-gray-800">
                            {line.count}
                          </span>
                        </div>
                    ))}
                    {data.topCruiseLines.length === 0 && <p className="text-sm text-gray-500">No data available</p>}
                  </div>
                  
                  {data.topCruiseLines.length > itemsPerPage && (
                    <div className="flex items-center justify-between border-t border-gray-100 pt-3 mt-3 shrink-0">
                      <button
                        onClick={() => setLinesPage(p => Math.max(1, p - 1))}
                        disabled={linesPage === 1}
                        className="text-sm font-medium text-blue-600 disabled:text-gray-400 hover:text-blue-800 transition-colors"
                      >
                        Previous
                      </button>
                      <span className="text-xs text-gray-500 font-medium">
                        Page {linesPage} of {Math.ceil(data.topCruiseLines.length / itemsPerPage)}
                      </span>
                      <button
                        onClick={() => setLinesPage(p => Math.min(Math.ceil(data.topCruiseLines.length / itemsPerPage), p + 1))}
                        disabled={linesPage === Math.ceil(data.topCruiseLines.length / itemsPerPage)}
                        className="text-sm font-medium text-blue-600 disabled:text-gray-400 hover:text-blue-800 transition-colors"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>

                <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm flex flex-col">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2 shrink-0">
                    <Anchor className="h-5 w-5 text-gray-500" />
                    Top Ships
                  </h3>
                  <div className="space-y-3 flex-1">
                    {data.topShips
                      .slice((shipsPage - 1) * itemsPerPage, shipsPage * itemsPerPage)
                      .map((ship, idx) => (
                        <div key={idx} className="flex justify-between items-center border-b border-gray-50 pb-2 last:border-0 last:pb-0">
                          <span className="text-sm text-gray-700 truncate pr-4">{ship.name}</span>
                          <span className="text-sm font-semibold bg-gray-100 px-2 py-0.5 rounded text-gray-800">
                            {ship.count}
                          </span>
                        </div>
                    ))}
                    {data.topShips.length === 0 && <p className="text-sm text-gray-500">No data available</p>}
                  </div>
                  
                  {data.topShips.length > itemsPerPage && (
                    <div className="flex items-center justify-between border-t border-gray-100 pt-3 mt-3 shrink-0">
                      <button
                        onClick={() => setShipsPage(p => Math.max(1, p - 1))}
                        disabled={shipsPage === 1}
                        className="text-sm font-medium text-blue-600 disabled:text-gray-400 hover:text-blue-800 transition-colors"
                      >
                        Previous
                      </button>
                      <span className="text-xs text-gray-500 font-medium">
                        Page {shipsPage} of {Math.ceil(data.topShips.length / itemsPerPage)}
                      </span>
                      <button
                        onClick={() => setShipsPage(p => Math.min(Math.ceil(data.topShips.length / itemsPerPage), p + 1))}
                        disabled={shipsPage === Math.ceil(data.topShips.length / itemsPerPage)}
                        className="text-sm font-medium text-blue-600 disabled:text-gray-400 hover:text-blue-800 transition-colors"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column: Leaderboard */}
            <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm lg:row-span-2 flex flex-col h-full">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2 shrink-0">
                <Trophy className="h-5 w-5 text-yellow-500" />
                Top Users (Most Cruises)
              </h3>
              
              <div className="space-y-4 flex-1">
                {data.topUsers
                  .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                  .map((user, idx) => {
                    const globalIdx = (currentPage - 1) * itemsPerPage + idx;
                    return (
                      <div key={user.userId} className="flex items-start gap-3 border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                        <div className={`
                          flex h-8 w-8 shrink-0 items-center justify-center rounded-full font-bold text-sm
                          ${globalIdx === 0 ? 'bg-yellow-100 text-yellow-700' : 
                            globalIdx === 1 ? 'bg-gray-100 text-gray-700' : 
                            globalIdx === 2 ? 'bg-orange-100 text-orange-800' : 'bg-blue-50 text-blue-700'}
                        `}>
                          {globalIdx + 1}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate">{user.displayName}</p>
                          <p className="text-xs text-gray-500 truncate">{user.email}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10 mb-1">
                            {user.cruiseCount} total
                          </span>
                          <div className="text-[10px] text-gray-500 font-medium">
                            {user.pastCruises} past • {user.futureCruises} future
                          </div>
                        </div>
                      </div>
                    );
                })}
                {data.topUsers.length === 0 && <p className="text-sm text-gray-500 text-center py-4">No users found</p>}
              </div>

              {/* Pagination Controls */}
              {data.topUsers.length > itemsPerPage && (
                <div className="flex items-center justify-between border-t border-gray-100 pt-4 mt-4 shrink-0">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="text-sm font-medium text-blue-600 disabled:text-gray-400 hover:text-blue-800 transition-colors"
                  >
                    Previous
                  </button>
                  <span className="text-xs text-gray-500 font-medium">
                    Page {currentPage} of {Math.ceil(data.topUsers.length / itemsPerPage)}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(Math.ceil(data.topUsers.length / itemsPerPage), p + 1))}
                    disabled={currentPage === Math.ceil(data.topUsers.length / itemsPerPage)}
                    className="text-sm font-medium text-blue-600 disabled:text-gray-400 hover:text-blue-800 transition-colors"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>

          </div>
        </div>
      ) : null}
    </motion.div>
  );
};

export default AdminAnalytics;
